import { HttpClient } from '@angular/common/http';
import { Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { Observable, catchError, firstValueFrom, map, of, Subscription, throwError } from 'rxjs';
import { delay } from 'rxjs/operators';
import { environment } from 'src/environments/environment';

import { AuthUser } from '../models/authUser';
import { LoginRequestModel } from '../models/loginRequestModel';
import { LoginResponseModel } from '../models/loginResponseModel';
import { RefreshTokenRequest } from '../models/refreshTokenRequest';
import { TokenRefreshResponse } from '../models/tokenRefreshResponse';
import { User } from '../models/user';
import { SessionTimeoutDialogComponent } from '../components/session-timeout-dialog/session-timeout-dialog.component';
import { TokenService } from './token.service';
import { PushNotificationService } from './push-notification.service';

declare global {
  interface Window {
    clarity: {
      (command: string, key: string, value: any): void;
    };
  }
}

@Injectable({
  providedIn: 'root',
})
export class AuthService implements OnDestroy {
  private _baseUrl = environment.baseUrl + '/authorization';
  private _user: AuthUser | undefined;
  private redirectUrl: string | null = null;
  private inactivityTimer: any;
  private readonly INACTIVITY_TIMEOUT = 60 * 60 * 1000 ; //60 * 60 * 1000;  // 1 hour
  private activitySubscription: Subscription = new Subscription();

  constructor(
    private http: HttpClient,
    private router: Router,
    private tokenService: TokenService,
    private modalService: NgbModal,
    private pushNotificationService: PushNotificationService
  ) {
    console.log('AuthService constructor called');
    console.log('Initial redirectUrl from sessionStorage:', sessionStorage.getItem('redirectUrl'));
    this.getUser();
    this.initializeInactivityTracking();
  }

  ngOnDestroy(): void {
    this.clearInactivityTimer();
    if (this.activitySubscription) {
      this.activitySubscription.unsubscribe();
    }
  }

  private initializeInactivityTracking(): void {
    if (this.isLoggedIn()) {
      this.resetInactivityTimer();

      // Set up event listeners for user activity
      const activityEvents = ['mousedown', 'mousemove', 'keypress', 'scroll', 'touchstart'];

      activityEvents.forEach(eventName => {
        window.addEventListener(eventName, this.resetInactivityTimer.bind(this));
      });
    }
  }

  private resetInactivityTimer(): void {
    this.clearInactivityTimer();

    if (this.isLoggedIn()) {
      this.inactivityTimer = setTimeout(() => {
        console.log('User inactive for 1 hour, logging out');

        // Set redirect url to the current url BEFORE logout
        this.setRedirectUrl(this.router.url);

        this.logout();

        // Show custom dialog instead of alert
        this.showSessionTimeoutDialog();
      }, this.INACTIVITY_TIMEOUT);
    }
  }

  private showSessionTimeoutDialog(): void {
    const modalRef = this.modalService.open(SessionTimeoutDialogComponent, {
      centered: true,
      backdrop: 'static',
      keyboard: false
    });

    // When dialog is closed, logout the user

  }

  private clearInactivityTimer(): void {
    if (this.inactivityTimer) {
      clearTimeout(this.inactivityTimer);
      this.inactivityTimer = null;
    }
  }

  setUser(user: LoginResponseModel): AuthUser | string[] {
    if (user.isSuccess) {
      this._user = {
        userName: user.result.username,
        token: user.result.token,
        tokenTo3d: user.result.tokenTo3d,
        expiration: new Date(user.result.expiration),
        refreshToken: user.result.refreshToken,
        roles: this.getUserRole(user.result.token),
        client: user.result.client,
        servicePermissions: user.result.servicePermissions ?? [],
      };
      localStorage.setItem('loggedInUser', JSON.stringify(this._user));
      this.initializeInactivityTracking();

      // Set Clarity user identification
      if (window.clarity && this._user.userName) {
        window.clarity('set', 'user_id', this._user.userName);
      }

      // Initialize push notifications after successful login
      this.initializePushNotifications();

      return this._user;
    }
    return user.messages.map((e) => e.content);
  }

  login(model: LoginRequestModel): Observable<AuthUser | string[]> {
    return this.http
      .post<LoginResponseModel>(`${this._baseUrl}/login`, model)
      .pipe(map((response: LoginResponseModel) => this.setUser(response)));
  }

  /**
   * Establishes a temporary authenticated session for contractors accessing
   * an RFP via invite link. The token comes from the backend RfpContractorView.
   */
  setContractorSession(loginResponse: {
    token?: string;
    tokenTo3d?: string;
    refreshToken?: string;
    username?: string;
    name?: string;
    client?: any;
    servicePermissions?: string[];
    expiration?: string | Date;
  }): void {
    const token = loginResponse.token ?? '';
    this._user = {
      userName: loginResponse.username ?? loginResponse.name ?? '',
      token,
      tokenTo3d: loginResponse.tokenTo3d ?? token,
      expiration: loginResponse.expiration
        ? new Date(loginResponse.expiration as string)
        : new Date(Date.now() + 24 * 60 * 60 * 1000),
      refreshToken: loginResponse.refreshToken ?? '',
      roles: token ? this.getUserRole(token) : [],
      client: loginResponse.client,
      servicePermissions: loginResponse.servicePermissions ?? [],
    };
    localStorage.setItem('loggedInUser', JSON.stringify(this._user));
  }

  logout() {
    this.clearInactivityTimer();



    this._user = undefined;
    localStorage.setItem('loggedInUser', '');

    // Clear Clarity user identification
    if (window.clarity) {
      window.clarity('set', 'user_id', null);
    }

    this.router.navigate(['/login']);
    return of({ success: true, roles: '' });
  }

  isLoggedIn() {
    return this._user ? true : false;
  }

  // check expiration of our token
  isTokenExpired() {
    const token: string = this._user?.token ?? '';
    if (!token) return false;
    const tokenSplit: string = token.split('.')[1];
    const decodedString: string = atob(tokenSplit);
    const jsonString = JSON.parse(decodedString);
    const expiry = jsonString.exp;
    return Math.floor(new Date().getTime() / 1000) >= expiry;
  }

  // Refresh token method
  refreshToken(): Observable<any> {
    //get user from local storage
    if (!this._user) {
      const user = localStorage.getItem('loggedInUser');
      if (user) {
        this._user = JSON.parse(user);
      }
    }

    if (!this._user || !this._user.token || !this._user.refreshToken) {
      return throwError(() => new Error('No valid tokens available'));
    }

    const refreshRequest: RefreshTokenRequest = {
      token: this._user.token,
      refreshToken: this._user.refreshToken
    };

    return this.tokenService.generateRefreshToken(refreshRequest).pipe(
      map((response) => {
        if (response.isSuccess && response.result) {
          // Update user tokens
          if (this._user) {
            this._user.token = response.result.token;
            this._user.refreshToken = response.result.refreshToken;
            this._user.tokenTo3d = response.result.tokenTo3D;

            // Update localStorage
            localStorage.setItem('loggedInUser', JSON.stringify(this._user));
          }
          return this._user;
        } else {
          throw new Error(response.messages?.map(m => m.content).join(', ') || 'Token refresh failed');
        }
      }),
      catchError((error) => {
        console.error('Token refresh error:', error);
        this.logout();
        return throwError(() => error);
      })
    );
  }

  public getRoles(): string[] {
    let roles = this.getUser()?.roles ?? [];
    if (typeof roles === 'string')
      roles = [roles];

    return roles;
  }

  public isAdmin(): boolean {
    return this.hasRoles('ADMIN');
  }

  public hasRoles(...roles: string[]): boolean {
    const userRoles = this.getRoles();
    //console.trace('hasRoles', userRoles, roles);
    return userRoles.some(r => roles.includes(r));
  }

  public getUserName(): string {
    return this.getUser()?.userName ?? '';
  }

  public getUserToken3d(): string {
    return this.getUser()?.tokenTo3d ?? '';
  }

  public getAuthBearerToken() {
    return this.getUser()?.token;
  }

  public getClientDataStructure() {
    return this.getUser()?.client;
  }

  public getClientName() {
    return this.getUser()?.client?.name;
  }

  public updateClientInfo(client: any) {
    if (this._user) {
      this._user.client = client;
      localStorage.setItem('loggedInUser', JSON.stringify(this._user));
    }
  }

  private getUser(): AuthUser | undefined {
    const storedUser = localStorage.getItem('loggedInUser');
    if (storedUser) {
      this._user = JSON.parse(storedUser);
    }

   // if (this.isTokenExpired()) {
     // localStorage.setItem('loggedInUser', '');
      //this._user = undefined;
    //}

    return this._user;
  }

  private getUserRole(token: string): string[] {
    const helper = new JwtHelperService();
    const decodedToken = helper.decodeToken(token ?? '');
    if (decodedToken) {
      const roles = decodedToken['http://schemas.microsoft.com/ws/2008/06/identity/claims/role'];

      if (Array.isArray(roles))
        return roles;
      return [roles];
    }
    return [];
  }

  // refreshing the access token (legacy method for backward compatibility)
  async refreshingToken(): Promise<boolean> {
    try {
      const result = await firstValueFrom(this.refreshToken());
      return !!result;
    } catch (error) {
      console.error("Error refreshing token:", error);
      return false;
    }
  }

  // Get current user data
  getCurrentUser(): Observable<User> {
    // If we have a user in memory, return it
    if (this._user) {
      const user: User = {
        id: "current-user-id",
        name: this._user.userName || "John Doe",
        email: this._user.userName,
        role: 1,
        active: true,
        client: this._user.client
      };
      return of(user);
    }

    // Otherwise return a mock user
    return of({
      id: "current-user-id",
      name: "John Doe",
      email: "john.doe@example.com",
      role: 1,
      active: true
    });
  }

  // Update user profile
  updateUserProfile(user: User): Observable<any> {
    // In a real implementation, this would make an API call
    // For now, just simulate success after a delay
    return of({ success: true }).pipe(
      delay(800)
    );
  }

  /**
   * Initialize push notifications for the logged-in user
   */
  private async initializePushNotifications(): Promise<void> {
    try {
      if (this.pushNotificationService.isEnabled) {
        // Initialize push notifications
        await this.pushNotificationService.initializePushNotifications();

        // Automatically request permission and subscribe after login
        await this.autoSubscribeToNotifications();

        console.log('Push notifications initialized for user:', this._user?.userName);
      }
    } catch (error) {
      console.error('Failed to initialize push notifications:', error);
    }
  }

  /**
   * Automatically subscribe user to push notifications after authorization
   */
  private async autoSubscribeToNotifications(): Promise<void> {
    try {
      console.log('🔔 Starting auto-subscription process for user:', this._user?.userName);

      // Check if user wants auto-subscription
      if (!this.pushNotificationService.shouldAutoSubscribe()) {
        console.log('⏭️ Auto-subscription is disabled by user preferences');
        return;
      }

      // Check current permission status
      const currentPermission = this.pushNotificationService.checkPermission();
      console.log('🔐 Current notification permission:', currentPermission);

      if (currentPermission === 'denied') {
        console.log('❌ Push notifications are denied by user');
        return;
      }

      let permission: NotificationPermission = currentPermission;

      // Request permission if not already granted
      if (permission === 'default') {
        console.log('📋 Requesting notification permission...');
        permission = await this.pushNotificationService.requestPermission();
        console.log('📋 Permission request result:', permission);
      }

      // Subscribe if permission is granted
      if (permission === 'granted') {
        console.log('✅ Permission granted, subscribing to push notifications...');
        const subscription = await this.pushNotificationService.requestSubscription();

        if (subscription) {
          console.log('🎉 User successfully subscribed to push notifications');
          console.log('📡 Subscription endpoint:', subscription.endpoint);

          // Send subscription to server
          console.log('🚀 Sending subscription to server...');
          const serverResult = await this.pushNotificationService.sendSubscriptionToServer(
            subscription,
            this._user?.userName
          );

          if (serverResult) {
            console.log('✅ Subscription successfully sent to server');

            // Save success status to localStorage
            localStorage.setItem('pushNotificationStatus', JSON.stringify({
              subscribed: true,
              subscribedAt: new Date().toISOString(),
              userId: this._user?.userName,
              endpoint: subscription.endpoint
            }));
          } else {
            console.warn('⚠️ Failed to send subscription to server, but local subscription is active');
          }


        } else {
          console.warn('❌ Failed to subscribe to push notifications');
        }
      } else {
        console.log('❌ Push notification permission not granted:', permission);
      }
    } catch (error) {
      console.error('💥 Error during auto-subscription to push notifications:', error);
    }
  }

  /**
   * Get push notification subscription status
   */
  getPushNotificationSubscriptionStatus(): any {
    const status = localStorage.getItem('pushNotificationStatus');
    if (status) {
      try {
        return JSON.parse(status);
      } catch (error) {
        console.error('Error parsing push notification status:', error);
      }
    }
    return null;
  }

  /**
   * Get current push notification status
   */
  async getPushNotificationStatus(): Promise<any> {
    if (this.pushNotificationService.isEnabled) {
      return await this.pushNotificationService.getSubscriptionStatus();
    }
    return {
      isSupported: false,
      permission: 'denied' as NotificationPermission,
      isSubscribed: false,
      subscription: null,
      preferences: null
    };
  }

  /**
   * Enable/disable auto-subscription for future logins
   */
  setNotificationPreferences(enabled: boolean, autoSubscribe: boolean = true): void {
    this.pushNotificationService.saveNotificationPreferences(enabled, autoSubscribe);
    console.log('Notification preferences updated:', { enabled, autoSubscribe });
  }

  /**
   * Send a welcome notification to the user
   */
  sendWelcomeNotification(): void {
    if (this._user && this.pushNotificationService.checkPermission() === 'granted') {
      this.pushNotificationService.showLocalNotification({
        title: 'Welcome to Builtgenix! 🎉',
        body: `Hello ${this._user.userName}! You're now subscribed to notifications and ready to go.`,
        icon: '/assets/icons/icon-192x192.png',
        data: {
          type: 'welcome',
          userId: this._user.userName,
          timestamp: new Date().toISOString(),
          url: '/'
        }
      });
    }
  }

  /**
   * Send notification when user logs out
   */
  private sendLogoutNotification(): void {
    if (this._user && this.pushNotificationService.checkPermission() === 'granted') {
      this.pushNotificationService.showLocalNotification({
        title: 'Logged Out',
        body: 'You have been successfully logged out from Builtgenix.',
        icon: '/assets/icons/icon-192x192.png',
        data: {
          type: 'logout',
          userId: this._user.userName,
          timestamp: new Date().toISOString()
        }
      });
    }
  }

  // Redirect URL methods
  setRedirectUrl(url: string): void {
    console.log('setRedirectUrl called with:', url);
    this.redirectUrl = url;
    sessionStorage.setItem('redirectUrl', url);
    console.log('redirectUrl saved to sessionStorage:', sessionStorage.getItem('redirectUrl'));
  }

  getRedirectUrl(): string | null {
    const memoryUrl = this.redirectUrl;
    const storageUrl = sessionStorage.getItem('redirectUrl');
    console.log('getRedirectUrl - memory:', memoryUrl, 'storage:', storageUrl);

    if (this.redirectUrl) {
      return this.redirectUrl;
    }
    return sessionStorage.getItem('redirectUrl');
  }

  clearRedirectUrl(): void {
    console.log('clearRedirectUrl called');
    this.redirectUrl = null;
    sessionStorage.removeItem('redirectUrl');
  }

  public hasServicePermission(permission: string): boolean {
    const userPermissions = this.getUser()?.servicePermissions ?? [];
    return userPermissions.includes(permission);
  }
}
